<template>
  <div v-if="show" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-fade-in">
    <div class="bg-white dark:bg-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden flex flex-col max-h-[95vh]">

      <!-- Encabezado -->
      <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/30">
        <div>
          <h2 class="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Bot class="w-5 h-5 text-indigo-500" />
            {{ form.id ? 'Configurar Agente IA' : 'Nuevo Agente IA' }}
          </h2>
          <p class="text-xs text-slate-500">Define su identidad, prompt de ventas y conexión a WhatsApp.</p>
        </div>
        <button @click="$emit('close')" class="p-2 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-full transition-colors text-slate-500">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Pestañas -->
      <div class="flex overflow-x-auto bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
        <button v-for="tab in tabs" :key="tab.id" @click="activeTab = tab.id"
          :class="['px-6 py-3 text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-b-2',
          activeTab === tab.id ? 'border-indigo-600 text-indigo-600 bg-white dark:bg-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700']">
          {{ tab.name }}
        </button>
      </div>

      <!-- Formulario -->
      <div class="flex-1 overflow-y-auto p-6 custom-scrollbar">
        <form id="agentForm" @submit.prevent="handleSubmit" class="space-y-6" novalidate>

          <!-- PESTAÑA 1: IDENTIDAD -->
          <div v-show="activeTab === 'identity'" class="space-y-6 animate-fade-in">
            <div class="bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800">

              <!-- Avatar Rápido -->
              <div class="flex items-center gap-4 mb-6">
                <img :src="form.photo_url || 'https://ui-avatars.com/api/?name=AI&background=1e1b4b&color=fff'" class="w-16 h-16 rounded-xl object-cover shadow-sm border border-slate-200 dark:border-slate-700" />
                <div class="flex-1">
                  <label :class="labelClasses">URL de la Foto (Avatar)</label>
                  <input v-model="form.photo_url" type="url" :class="inputClasses" placeholder="https://ejemplo.com/foto.jpg" />
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label :class="labelClasses">* Nombre del Agente</label>
                  <input v-model="form.name" type="text" :class="inputClasses" placeholder="Ej: Sophia" required />
                </div>
                <div>
                  <label :class="labelClasses">Estado del Agente</label>
                  <select v-model="form.state" :class="inputClasses">
                    <option :value="1">Activo (Responde Mensajes)</option>
                    <option :value="0">Pausado (No Responde)</option>
                  </select>
                </div>
                <div>
                  <label :class="labelClasses">Edad</label>
                  <input v-model="form.age" type="number" :class="inputClasses" placeholder="Ej: 28" />
                </div>
                <div>
                  <label :class="labelClasses">Género</label>
                  <select v-model="form.gender" :class="inputClasses">
                    <option :value="1">Femenino</option>
                    <option :value="2">Masculino</option>
                    <option :value="3">No binario / Robot</option>
                  </select>
                </div>
                <div class="md:col-span-2">
                  <label :class="labelClasses">Biografía Corta (Para ti)</label>
                  <textarea v-model="form.bio" rows="2" :class="inputClasses" placeholder="Ej: Especialista en ventas de FrontLine..."></textarea>
                </div>
              </div>
            </div>
          </div>

          <!-- PESTAÑA 2: CEREBRO E IA (SYSTEM PROMPT) -->
          <div v-show="activeTab === 'brain'" class="space-y-6 animate-fade-in">
            <div class="bg-indigo-50/50 dark:bg-indigo-900/10 p-5 rounded-xl border border-indigo-100 dark:border-indigo-500/20">
              <h3 class="text-sm font-bold text-indigo-900 dark:text-indigo-400 flex items-center gap-2 mb-4">
                <Brain class="w-4 h-4" /> Comportamiento del Modelo (Llama 3.3 / Groq)
              </h3>

              <div>
                <label :class="labelClasses">* System Prompt (Instrucciones Inmutables)</label>
                <textarea
                  v-model="form.system_prompt"
                  rows="12"
                  class="w-full px-4 py-3 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all text-slate-800 dark:text-slate-300 custom-scrollbar"
                  placeholder="Actúa como un experto en ventas. Tus reglas son: 1. Ser amable. 2. Nunca inventar precios..."
                  required
                ></textarea>
                <p class="text-[10px] text-slate-500 mt-2">
                  Este es el texto que define el blindaje y personalidad de tu IA. Usa un lenguaje claro y reglas enumeradas.
                </p>
              </div>
            </div>
          </div>

          <!-- PESTAÑA 3: WHATSAPP (EVOLUTION API) -->
          <div v-show="activeTab === 'integration'" class="space-y-6 animate-fade-in">
            <div class="bg-slate-50/50 dark:bg-slate-800/30 p-5 rounded-xl border border-slate-100 dark:border-slate-800">

              <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2 mb-4">
                <MessageCircle class="w-4 h-4 text-emerald-500" /> Asignación de Canal
              </h3>

              <div>
                <label :class="labelClasses">Instancia de Evolution API</label>
                <select v-model="form.instance_name" :class="inputClasses">
                  <option value="">-- Sin Instancia Asignada --</option>

                  <!-- Iteramos las instancias recibidas por props -->
                  <option
                    v-for="inst in evolutionInstances"
                    :key="inst.id"
                    :value="inst.name"
                  >
                    {{ inst.name }} ({{ inst.ownerJid ? inst.ownerJid.split('@')[0] : 'Desconectado' }})
                  </option>
                </select>
                <p class="text-[10px] text-slate-500 mt-2">
                  Selecciona qué número de WhatsApp controlará este agente. Las instancias se obtienen en tiempo real de tu servidor.
                </p>
              </div>

              <!-- Extra (Campo Gallery Json) -->
              <div class="mt-6">
                <label :class="labelClasses">Galería / Contexto Extra (JSON)</label>
                <textarea v-model="form.gallery" rows="3" class="w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500/20 text-slate-500" placeholder='{"links": []}'></textarea>
              </div>
            </div>
          </div>

        </form>
      </div>

      <!-- Pie del Modal -->
      <div class="px-6 py-4 bg-white dark:bg-slate-900 border-t border-slate-100 dark:border-slate-800 flex justify-between items-center z-20">
        <div class="flex gap-3 w-full sm:w-auto justify-end ml-auto">
          <button @click="$emit('close')" type="button" :class="btnSecondary">Cancelar</button>
          <button v-if="activeTab !== 'integration'" @click="nextTab" type="button" :class="btnPrimary">Siguiente</button>
          <button v-else type="submit" form="agentForm" :disabled="isSaving" :class="btnPrimary">
            <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
            <Save v-else class="w-4 h-4" />
            Guardar Agente
          </button>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch } from 'vue';
import { X, Save, Loader2, Bot, Brain, MessageCircle } from 'lucide-vue-next';
import { useToast } from '@/composables/useToast';
import apiClient from '@/api/client';
import axios from 'axios';
const BASE_URL = 'https://agentes.therealdevs.pro/index.php/';
const EVO_URL = 'https://wapi.therealdevs.pro';
const EVO_KEY = 'TheRealDevsPro2026';
const props = defineProps({
  show: Boolean,
  agentToEdit: Object,
  evolutionInstances: { type: Array, default: () => [] } // Recibe las instancias de Evolution
});
const emit = defineEmits(['close', 'saved']);
const { showSuccess, showError } = useToast();

const activeTab = ref('identity');
const isSaving = ref(false);

const tabs = [
  { id: 'identity', name: '1. Identidad' },
  //{ id: 'brain', name: '2. Prompt & Cerebro' },
  { id: 'integration', name: '3. Conexión WhatsApp' }
];

const inputClasses = "w-full px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all text-slate-700 dark:text-slate-200";
const labelClasses = "text-[10px] font-bold text-slate-500 uppercase mb-1 block";
const btnPrimary = "px-6 py-2.5 text-sm font-bold text-white bg-indigo-600 rounded-xl hover:bg-indigo-700 shadow-lg transition-all flex items-center justify-center gap-2 disabled:opacity-70 w-full sm:w-auto";
const btnSecondary = "px-4 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-xl hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 transition-colors w-full sm:w-auto";

const form = reactive({
  id: null,
  name: '',
  instance_name: '',
  age: 25,
  gender: 1,
  bio: '',
  photo_url: '',
  system_prompt: '',
  state: 1,
  gallery: ''
});

const nextTab = () => {
  const currentIndex = tabs.findIndex(tab => tab.id === activeTab.value);
  if (currentIndex < tabs.length - 1) activeTab.value = tabs[currentIndex + 1].id;
};

watch(() => props.show, (val) => {
  if (val) {
    activeTab.value = 'identity';
    if (props.agentToEdit) {
      Object.assign(form, props.agentToEdit);
    } else {
      Object.assign(form, {
        id: null, name: '', instance_name: '', age: 25, gender: 1, bio: '',
        photo_url: '', system_prompt: '', state: 1, gallery: ''
      });
    }
  }
});

const handleSubmit = async () => {
  isSaving.value = true;
  let savedAgentId=0;
  try {
    const payload = { ...form };
    if (!form.id) delete payload.id;

    if (form.id) {
      await apiClient.put(`/ai_characters/${form.id}`, payload);
      showSuccess('Agente actualizado correctamente');
    } else {
      const res = await apiClient.post('/ai_characters', payload);
      savedAgentId = res.data?.id || res.data?.data?.id;
      showSuccess('Nuevo Agente IA creado');
    }
    if (form.instance_name && savedAgentId) {
      const webhookUrl = `${BASE_URL}/webhook_whatsapp/${savedAgentId}`;

      const payloadWebhook = {
        webhook: {
          enabled: true,
          url: webhookUrl,
          webhookByEvents: true,
          base64: false,
          events: [
            "MESSAGES_UPSERT"
          ]
        }
      };

      await axios.post(`${EVO_URL}/webhook/set/${form.instance_name}`, payloadWebhook, {
        headers: { apikey: EVO_KEY }
      });

      const payloadSettings = {
        rejectCall: true,
        msgCall: "Las llamadas están deshabilitadas para este asistente IA.",
        groupsIgnore: true,
        alwaysOnline: true,
        readMessages: false,
        readStatus: false,
        syncFullHistory: false
      };

      await axios.post(`${EVO_URL}/settings/set/${form.instance_name}`, payloadSettings, {
        headers: { apikey: EVO_KEY }
      });

    }

    emit('saved');
    emit('close');
  } catch (error) {
    showError('Error al guardar el agente en la base de datos.');
  } finally {
    isSaving.value = false;
  }
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
.animate-fade-in { animation: fade-in 0.2s ease-out forwards; }
.custom-scrollbar::-webkit-scrollbar { width: 4px; }
.custom-scrollbar::-webkit-scrollbar-thumb { background-color: #cbd5e1; border-radius: 20px; }
.dark .custom-scrollbar::-webkit-scrollbar-thumb { background-color: #334155; }
</style>